<template>
  <div class="section" :class="{ expanded }">
    <div class="section-header">
      <div class="section-info">
        <span class="section-title">工作流管理</span>
      </div>
      <div class="section-controls">
        <button class="header-action" title="新建" @click="createWorkflow">
          <img src="@/assets/icon/addFlow.png" width="12" height="12" alt="">
        </button>
        <button class="header-action" title="导入" @click="importWorkflow">
          <img src="@/assets/icon/importFlow.png" width="10" height="10" alt="">
        </button>
        <button class="header-action" title="导出" :disabled="!hasWorkflow" @click="exportWorkflow">
          <img src="@/assets/icon/exportFlow.png" width="10" height="10" alt="">
        </button>
      </div>
    </div>
    <div class="section-content" v-show="expanded">
      <div class="workflow-list">
        <div
          class="workflow-item"
          v-for="workflow in workflowList"
          :key="workflow.id"
          :class="{ active: workflow.id === currentWorkflowId }"
          @click="loadWorkflow(workflow)"
        >
          <div class="workflow-info">
            <div class="workflow-name">{{ workflow.name }}</div>
            <div class="workflow-meta">{{ workflow.nodeCount }} 个节点</div>
          </div>
          <div class="workflow-actions">
            <ElDropdown trigger="click" placement="bottom-end" popper-class="mflow-dark-dropdown">
              <button class="item-menu-btn" @click.stop>
                <ElIcon><MoreFilled /></ElIcon>
              </button>
              <template #dropdown>
                <ElDropdownMenu class="workflow-menu">
                  <ElDropdownItem class="workflow-menu-item" @click="cloneWorkflow(workflow)">
                    <ElIcon class="menu-item-icon"><Document /></ElIcon>
                    <span>复制</span>
                  </ElDropdownItem>
                  <ElDropdownItem class="workflow-menu-item" @click="renameWorkflow(workflow)">
                    <ElIcon class="menu-item-icon"><Edit /></ElIcon>
                    <span>重命名</span>
                  </ElDropdownItem>
                  <ElDropdownItem class="workflow-menu-item danger" @click="deleteWorkflow(workflow)">
                    <ElIcon class="menu-item-icon"><Delete /></ElIcon>
                    <span>删除</span>
                  </ElDropdownItem>
                </ElDropdownMenu>
              </template>
            </ElDropdown>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useWorkflowStore } from 'mflows'
import {
  ElDropdown,
  ElDropdownMenu,
  ElDropdownItem,
  ElIcon,
  MoreFilled,
  Document,
  Edit,
  Delete,
  Download,
  Upload,
  Plus,
} from '@/plugins/elementPlus'
import { ElMessageBox, ElMessage } from '@/plugins/elementPlus'

defineProps<{ expanded: boolean }>()

const workflow = useWorkflowStore()

interface SavedWorkflowItem {
  id: string
  name: string
  nodeCount: number
  lastModified: Date
  data: { nodes: any[]; edges: any[] }
}
const WORKFLOWS_KEY = 'mflow_workflow_list'
const WORKFLOW_CURRENT_ID_KEY = 'mflow_current_workflow_id'

const workflowList = ref<SavedWorkflowItem[]>([])
const currentWorkflowId = ref<string>('')

function loadWorkflowsFromStorage(): SavedWorkflowItem[] {
  try {
    const raw = localStorage.getItem(WORKFLOWS_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw) as (Omit<SavedWorkflowItem, 'lastModified'> & { lastModified: string })[]
    return arr.map((it) => ({ ...it, lastModified: new Date(it.lastModified) }))
  } catch {
    return []
  }
}

function saveWorkflowsToStorage(list: SavedWorkflowItem[]) {
  try {
    const arr = list.map((it) => ({ ...it, lastModified: it.lastModified.toISOString() }))
    localStorage.setItem(WORKFLOWS_KEY, JSON.stringify(arr))
  } catch {}
}

// 持久化 currentWorkflowId
watch(
  () => currentWorkflowId.value,
  (id) => {
    try {
      localStorage.setItem(WORKFLOW_CURRENT_ID_KEY, id)
    } catch {}
  }
)

function ensureInitWorkflows() {
  const list = loadWorkflowsFromStorage()
  const lastId = localStorage.getItem(WORKFLOW_CURRENT_ID_KEY)
  if (list.length === 0) {
    const data = workflow.exportWorkflow()
    const item: SavedWorkflowItem = {
      id: 'default',
      name: '默认工作流',
      nodeCount: data.nodes.length,
      lastModified: new Date(),
      data,
    }
    workflowList.value = [item]
    currentWorkflowId.value = item.id
    workflow.setCurrentWorkflowId(item.id)
    saveWorkflowsToStorage(workflowList.value)
  } else {
    workflowList.value = list
    const target = lastId ? list.find((w) => w.id === lastId) || list[0] : list[0]
    currentWorkflowId.value = target.id
    workflow.importWorkflow(target.data.nodes as any, target.data.edges as any)
    workflow.setCurrentWorkflowId(target.id)
  }
}

ensureInitWorkflows()

const hasWorkflow = computed(() => workflow.state.nodes.length > 0)

function findWorkflowIndex(id: string) {
  return workflowList.value.findIndex((w) => w.id === id)
}

function saveCurrentWorkflowSnapshot() {
  const idx = findWorkflowIndex(currentWorkflowId.value)
  if (idx >= 0) {
    const data = workflow.exportWorkflow()
    workflowList.value[idx].data = data
    workflowList.value[idx].nodeCount = data.nodes.length
    workflowList.value[idx].lastModified = new Date()
    saveWorkflowsToStorage(workflowList.value)
  }
}

function createWorkflow() {
  saveCurrentWorkflowSnapshot()
  const id = `workflow_${Date.now()}`
  const newWorkflow: SavedWorkflowItem = {
    id,
    name: `工作流 ${workflowList.value.length}`,
    nodeCount: 0,
    lastModified: new Date(),
    data: { nodes: [], edges: [] },
  }
  workflowList.value.push(newWorkflow)
  currentWorkflowId.value = id
  workflow.setCurrentWorkflowId(id)
  workflow.importWorkflow([], [])
  saveWorkflowsToStorage(workflowList.value)
}

function loadWorkflow(workflowItem: SavedWorkflowItem) {
  if (currentWorkflowId.value === workflowItem.id) return
  saveCurrentWorkflowSnapshot()
  currentWorkflowId.value = workflowItem.id
  workflow.setCurrentWorkflowId(workflowItem.id)
  workflow.importWorkflow(workflowItem.data.nodes as any, workflowItem.data.edges as any)
}

function cloneWorkflow(workflowItem: SavedWorkflowItem) {
  const id = `workflow_${Date.now()}`
  const data = { nodes: [...workflowItem.data.nodes], edges: [...workflowItem.data.edges] }
  const cloned: SavedWorkflowItem = {
    id,
    name: `${workflowItem.name} (副本)`,
    nodeCount: data.nodes.length,
    lastModified: new Date(),
    data,
  }
  workflowList.value.push(cloned)
  saveWorkflowsToStorage(workflowList.value)
}

async function renameWorkflow(workflowItem: SavedWorkflowItem) {
  const { value } = await ElMessageBox.prompt('请输入新名称:', '重命名', { inputValue: workflowItem.name, confirmButtonText: '确定', cancelButtonText: '取消' }).catch(() => ({ value: null }))
  if (value && value.trim()) {
    workflowItem.name = value.trim()
    workflowItem.lastModified = new Date()
    saveWorkflowsToStorage(workflowList.value)
  }
}

async function deleteWorkflow(workflowItem: SavedWorkflowItem) {
  if (workflowList.value.length <= 1) {
    ElMessage.warning('至少需要保留一个工作流')
    return
  }
  try {
    await ElMessageBox.confirm(`确定删除工作流 "${workflowItem.name}" 吗？`, '提示', { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' })
  } catch { return }
  const index = findWorkflowIndex(workflowItem.id)
  if (index > -1) {
    workflowList.value.splice(index, 1)
    if (currentWorkflowId.value === workflowItem.id) {
      const target = workflowList.value[0]
      currentWorkflowId.value = target.id
      workflow.setCurrentWorkflowId(target.id)
      workflow.importWorkflow(target.data.nodes as any, target.data.edges as any)
    }
    saveWorkflowsToStorage(workflowList.value)
  }
}

function exportWorkflow() {
  saveCurrentWorkflowSnapshot()
  const currentIdx = findWorkflowIndex(currentWorkflowId.value)
  const data = currentIdx >= 0 ? workflowList.value[currentIdx].data : workflow.exportWorkflow()
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `workflow_${Date.now()}.json`
  a.click()
  URL.revokeObjectURL(url)
}

function importWorkflow() {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.json'
  input.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string)
          workflow.importWorkflow(data.nodes || [], data.edges || [])
          const idx = findWorkflowIndex(currentWorkflowId.value)
          if (idx >= 0) {
            workflowList.value[idx].data = workflow.exportWorkflow()
            workflowList.value[idx].nodeCount = workflowList.value[idx].data.nodes.length
            workflowList.value[idx].lastModified = new Date()
            saveWorkflowsToStorage(workflowList.value)
          }
        } catch (err) {
          ElMessage.error('文件格式错误')
        }
      }
      reader.readAsText(file)
    }
  }
  input.click()
}

// 自动保存（去抖）
let saveTimer: ReturnType<typeof setTimeout> | null = null
let lastSavedSignature = ''
function scheduleSaveSnapshot() {
  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(() => {
    try {
      const data = workflow.exportWorkflow()
      const signature = JSON.stringify({ n: data.nodes, e: data.edges })
      if (signature === lastSavedSignature) return
      saveCurrentWorkflowSnapshot()
      lastSavedSignature = signature
    } catch {}
  }, 600)
}

watch(
  () => workflow.state.nodes,
  () => scheduleSaveSnapshot(),
  { deep: true, flush: 'post' }
)
watch(
  () => workflow.state.edges,
  () => scheduleSaveSnapshot(),
  { deep: true, flush: 'post' }
)
</script>

<style scoped lang="scss">
.section {
  margin: 0;
  border-radius: 6px;
  background: #1f1f1f;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  &.expanded {
    flex: 1;
    min-height: 0;
  }
}
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  // background: #2a2d2e;
  border-bottom: 1px solid #333333;
  cursor: pointer;
  user-select: none;
}
.section-info { display: flex; align-items: center; gap: 8px; flex: 1; }
.section-title { color: #cccccc; font-size: 12px; font-weight: 500; }
.section-controls { display: flex; align-items: center; gap: 6px; height: 30px; }
.header-action { background: #333333; border:none;width: 20px; height: 20px; color: #bbb; border-radius: 2px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.header-action:disabled { opacity: 0.5; cursor: not-allowed; }
.header-action:hover { background: #333; color: #fff; border-color: #666; }
/* 移除局部箭头样式以继承全局 components.scss 定义 */
// .section-arrow { color: #888; font-size: 10px; transition: transform 0.2s; }
// .section-arrow.expanded { transform: rotate(180deg); }
.section-content { padding: 6px; flex: 1; overflow-y: auto; background: #1e1e1e; }

.workflow-list { display: flex; flex-direction: column; gap: 6px; }
.workflow-item { display: flex; align-items: center; justify-content: space-between; padding: 6px 8px; background: #2a2a2a; border: 1px solid transparent; border-radius: 2px; cursor: pointer; transition: all 0.2s; }
.workflow-item:hover { background: #333; border-color: #444; }
.workflow-item.active { background: rgba(0,137,255,0.06); border-color: #0089FF; }
.workflow-info { flex: 1; min-width: 0; }
.workflow-name { color: #cccccc; font-size: 11px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.workflow-meta { color: #888; font-size: 10px; margin-top: 2px; }
.item-menu-btn { background: none; border: none; color: #bbb; padding: 4px; border-radius: 4px; cursor: pointer; }
.item-menu-btn:hover { background: #333; color: #fff; border-color: #666; }

/* 下拉菜单样式统一由全局 mflow-dark-dropdown 管理 */
</style>
